'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { FillScreen } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography';
import { RuleInclusionsSection } from '@/features/device-assignment-rule/components';
import { useDeviceAssignmentRuleDetail, useDeviceAssignmentRuleGroupOptions, useUpdateDeviceAssignmentRuleMutation } from '@/features/device-assignment-rule/query';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import type { DeviceAssignmentRuleDetail } from '@/lib/api/orpc/contracts/device-assignment-rule';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { RuleAssignmentPreviewSection } from '../-components/RuleAssignmentPreviewSection';
import { RuleForm } from '../-components/RuleForm';
import { RuleFormPage } from '../-components/RuleFormPage';
import { RuleDetailView } from './-components/RuleDetailView';
export const Route = createFileRoute('/_staff/devices/_list/rule-and-policy/$ruleId/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    ruleId
  } = Route.useParams();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.ruleAndPolicy'
  });
  const {
    data: rule,
    isPending,
    isError
  } = useDeviceAssignmentRuleDetail(ruleId);
  if (isPending) {
    return <FillScreen className="flex min-h-0 flex-col overflow-hidden">
        <ViewportLoader />
      </FillScreen>;
  }
  if (isError || !rule) {
    return <FillScreen className="flex min-h-0 flex-col overflow-hidden">
        <Text variant="p" className="p-6 text-error-on-neutral-fill">
          {t('loadError')}
        </Text>
      </FillScreen>;
  }
  return <FillScreen className="flex min-h-0 flex-col overflow-hidden">
      <RuleDetailPage key={ruleId} ruleId={ruleId} rule={rule} />
    </FillScreen>;
}
type RuleDetailPageProps = {
  ruleId: string;
  rule: DeviceAssignmentRuleDetail;
};
function RuleDetailPage({
  ruleId,
  rule
}: RuleDetailPageProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.devices.ruleAndPolicy'
  });
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const {
    data: ruleGroups = [],
    isError: isRuleGroupsError
  } = useDeviceAssignmentRuleGroupOptions();
  const mutation = useUpdateDeviceAssignmentRuleMutation({
    onSuccess: () => setIsEditing(false)
  });
  const ruleGroupName = rule.ruleGroupId == null ? null : ruleGroups.find(group => group.id === rule.ruleGroupId)?.name ?? null;
  const displayName = rule.name;
  useDocumentTitle(isEditing ? t('editRule') : t('detailTitle', {
    name: displayName
  }));
  const goToList = () => navigate({
    to: '/devices/rule-and-policy'
  });
  const handleCancelEdit = () => {
    setFormKey(key => key + 1);
    setIsEditing(false);
  };
  return <RuleFormPage title={isEditing ? t('editRule') : t('detailTitle', {
    name: displayName
  })} onBack={goToList} mode={isEditing ? 'edit' : 'view'} onEdit={() => setIsEditing(true)} onCancel={handleCancelEdit} isSubmitting={mutation.isPending}>
      <div className="flex flex-col gap-7">
        {isEditing ? <RuleForm key={formKey} initial={{
        name: rule.name,
        description: rule.description,
        deviceGroupId: rule.deviceGroupId,
        peopleGroupIds: rule.peopleGroupIds,
        ruleGroupId: rule.ruleGroupId,
        orderInGroup: rule.orderInGroup
      }} initialPeopleGroups={rule.peopleGroups} initialDeviceGroup={rule.deviceGroup} ruleGroups={ruleGroups} ruleGroupsLoadError={isRuleGroupsError} onSubmit={body => mutation.mutate({
        ...body,
        ruleId
      })} /> : <RuleDetailView rule={rule} ruleGroupName={ruleGroupName} displayName={displayName} />}
        {!isEditing ? <RuleInclusionsSection mode="rule" ruleId={ruleId} /> : null}
        {!isEditing ? <RuleAssignmentPreviewSection draft={{
        peopleGroupIds: rule.peopleGroupIds,
        deviceGroupId: rule.deviceGroupId
      }} /> : null}
      </div>
    </RuleFormPage>;
}